<script setup>
/**
 * GlTabs — horizontally tabbed navigation.
 *
 * Props are designed to be forward-compatible with GlTabs when @gitlab/ui
 * gains native Vue 3 support:
 *
 *   - `tabs`        — array of { id: String, label: String, title?: String, count?: Number }
 *   - `modelValue`  — currently active tab index (Number), matching GlTabs'
 *                     v-model convention.  Callers that need the active tab ID
 *                     can derive it as `tabs[modelValue].id`.
 *   - `contentClass` — additional CSS class for the nav container
 *   - `justified`    — when true, tabs stretch to fill available width
 *
 * Emits `update:modelValue` with the new index on tab click.
 */
const { contentClass, justified, modelValue, tabs } = defineProps({
  contentClass: {
    default: null,
    type: String
  },
  justified: {
    default: false,
    type: Boolean
  },
  modelValue: {
    default: 0,
    type: Number
  },
  tabs: {
    required: true,
    type: Array
  }
});

const emit = defineEmits(["update:modelValue"]);
</script>

<template>
  <div class="gl-tabs-wrapper">
    <nav :class="['gl-tabs-nav', contentClass]">
      <ul class="flex list-none p-0 m-0">
        <li v-for="(tab, index) in tabs" :key="tab.id" :class="{ 'flex-1': justified }">
          <button
            type="button"
            :title="tab.title"
            :data-testid="`tab-${tab.id}`"
            :class="['gl-tab-nav-item', modelValue === index && 'gl-tab-nav-item-active']"
            @click="emit('update:modelValue', index)"
          >
            {{ tab.label }}
            <span v-if="tab.count != null" class="gl-tab-counter-badge gl-badge badge-muted" aria-hidden="true">
              <span class="gl-badge-content">{{ tab.count }}</span>
            </span>
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>
